import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { CheckoutFormState } from "@/pages/checkout/stores/use-checkout-store";

export type ValidationErrors = Partial<Record<keyof CheckoutFormState, string>>;

type FieldValidator = (value: string | boolean, form: CheckoutFormState) => string | undefined;

const PHONE_NUMBER_REGEX = /^[0-9\s\-().]+$/;

const REQUIRED_FIELDS: (keyof CheckoutFormState)[] = [
  "businessJustification",
  "costCenter",
  "managerConfirmationCheckbox",
  "shippingDetailsConfirmationCheckbox",
  "policyConfirmationCheckbox",
  "fullName",
  "country",
  "addressLine1",
  "citySubdivision1",
  "city",
  "postalCode",
  "countryCode",
  "phoneNumber",
];

const fieldValidators: Partial<Record<keyof CheckoutFormState, FieldValidator>> = {
  phoneNumber: (value) =>
    !PHONE_NUMBER_REGEX.test(value as string)
      ? getString("checkout.formPanel.shippingDetailsCard.invalidPhoneNumber")
      : undefined,
};

const isFieldEmpty = (value: string | boolean): boolean => {
  if (typeof value === "boolean") return !value;
  return value.trim() === "";
};

export const hasRequiredFields = (
  form: CheckoutFormState,
  isDeviceReturnRequired: boolean
): boolean => {
  const fields = isDeviceReturnRequired
    ? [...REQUIRED_FIELDS, "serialNumberForReturnDevice" as keyof CheckoutFormState]
    : REQUIRED_FIELDS;

  return fields.every((field) => !isFieldEmpty(form[field]));
};

export const isFormValid = (
  form: CheckoutFormState,
  isDeviceReturnRequired: boolean
): { isValid: boolean; errors: ValidationErrors } => {
  const errors: ValidationErrors = {};

  const requiredFields = isDeviceReturnRequired
    ? [...REQUIRED_FIELDS, "serialNumberForReturnDevice" as keyof CheckoutFormState]
    : REQUIRED_FIELDS;

  for (const field of requiredFields) {
    if (isFieldEmpty(form[field])) {
      errors[field] = getString("checkout.formPanel.requiredFieldError");
    }
  }

  for (const [field, validate] of Object.entries(fieldValidators)) {
    const key = field as keyof CheckoutFormState;
    if (errors[key]) continue;
    const error = validate(form[key], form);
    if (error) {
      errors[key] = error;
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};
